import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import LegalLayout from "@/components/LegalLayout";

/**
 * DRAFTS — Worldwide-launch legal addenda for ATTORNEY REVIEW.
 *
 * Unlisted page (/legal/worldwide-drafts, noindex). The PUBLISHED Terms
 * of Service and Privacy Policy are untouched — these two addenda are
 * drafted as amendments that would attach to the existing documents
 * when (and only when) counsel approves the worldwide launch.
 *
 * Items flagged [LAWYER] are placeholders that require an attorney's
 * judgment — do not publish without resolving them.
 */

const H = ({ children }) => (
  <h2 className="font-spectral text-2xl tracking-tight mt-10 mb-3">{children}</h2>
);
const H3 = ({ children }) => (
  <h3 className="font-bold text-base mt-6 mb-2">{children}</h3>
);
const P = ({ children }) => (
  <p className="text-[14.5px] leading-relaxed text-[#3F3F3F] mb-3">{children}</p>
);
const LAWYER = ({ children }) => (
  <p className="text-[13px] leading-relaxed bg-amber-50 border border-amber-300 rounded-lg p-3 my-3 text-amber-900">
    <strong>[LAWYER]</strong> {children}
  </p>
);

export default function WorldwideLegalDrafts() {
  return (
    <LegalLayout
      eyebrow="DRAFT — Not in effect"
      title="Worldwide Launch — Legal Addenda (Attorney Review Drafts)"
      lastUpdated="June 2026 (draft v1)"
    >
      <Helmet>
        <title>DRAFT — Worldwide Legal Addenda · Armstrong K9 Registry</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div
        className="rounded-xl border-2 border-dashed border-crimson/50 bg-crimson/5 p-4 mb-8 text-sm leading-relaxed"
        data-testid="draft-banner"
      >
        <strong className="text-crimson">DRAFT FOR ATTORNEY REVIEW — NOT IN EFFECT.</strong>{" "}
        These addenda are proposals for the international launch. The current{" "}
        <Link to="/terms" className="underline">Terms of Service</Link> and{" "}
        <Link to="/privacy" className="underline">Privacy Policy</Link> remain
        the only documents in force. Use the print button above to produce a
        copy for counsel.
      </div>

      {/* ============ DRAFT 1 — TOS INTERNATIONAL ADDENDUM ============ */}
      <H>Draft 1 — International Addendum to the Terms of Service</H>
      <P>
        This Addendum supplements the Armstrong K9 Registry Terms of Service
        (the &ldquo;Terms&rdquo;) and applies to users who access the Services
        from outside the United States (&ldquo;International Users&rdquo;). If
        this Addendum conflicts with the Terms, this Addendum controls for
        International Users. Capitalized terms have the meanings given in the
        Terms.
      </P>

      <H3>1. Eligibility; Sanctions &amp; Export Compliance</H3>
      <P>
        The Services are not available to persons located in, organized under
        the laws of, or ordinarily resident in any jurisdiction subject to
        comprehensive United States sanctions (currently including Cuba, Iran,
        North Korea, Syria, and the Crimea, Donetsk, and Luhansk regions), nor
        to persons in Russia or Belarus, nor to any person on a U.S.
        government restricted-party list. You represent that none of these
        apply to you, and you agree we may suspend or terminate your account
        if we have reason to believe otherwise.
      </P>

      <H3>2. Buyer Services Only; No International Seller Payouts</H3>
      <P>
        International Users may register dogs, maintain pedigree records,
        purchase subscriptions and Registry services, and — where the
        International Listings program is enabled — pay listing fees to
        advertise dogs for sale. International Users may <strong>not</strong>{" "}
        receive payments or payouts through the platform. All transactions
        for animals listed by International Users occur directly between
        buyer and seller, off-platform (&ldquo;Contact-to-Buy&rdquo;), and the
        Registry's escrow, refund, and dispute services do not apply to them.
      </P>

      <H3>3. Venue Status; Local Animal Laws</H3>
      <P>
        Armstrong K9 Registry is a <strong>venue</strong>. We are not the
        seller, breeder, broker, shipper, or guarantor of any animal. Laws
        governing the breeding, sale, advertising, transport, import, and
        export of animals differ by country and may prohibit or restrict
        transactions that the platform technically permits you to advertise.
        You are solely responsible for compliance with all laws of your
        country and the buyer's country, including animal-welfare, consumer,
        veterinary, customs, and import/export requirements (e.g., rabies
        certification, microchipping, minimum transfer ages, CITES where
        applicable).
      </P>

      <H3>4. Prices, Currency &amp; Taxes</H3>
      <P>
        All prices are stated and charged in United States Dollars (USD).
        Your card issuer determines any currency-conversion rate and fees.
        Where required, value-added tax (VAT), goods-and-services tax (GST),
        or similar taxes will be calculated and added at checkout. You are
        responsible for any taxes or duties not collected by us.
      </P>

      <H3>5. EU/UK Consumers — Right of Withdrawal</H3>
      <P>
        If you are a consumer in the European Union or the United Kingdom,
        you have a statutory right to withdraw from a contract for digital
        services within 14 days without giving a reason. By purchasing a
        subscription, registration, certificate, course, or other digital
        service and requesting immediate performance, you expressly consent
        to performance beginning before the withdrawal period ends and
        acknowledge that you lose the right of withdrawal once the service
        is fully performed (for digital content, once supply has begun).
        Where the service is not fully performed, withdrawal entitles you to
        a pro-rated refund.
      </P>
      <LAWYER>
        Confirm withdrawal-waiver language against Directive 2011/83/EU art.
        16(m) and UK CCRs 2013 reg. 37, and whether the existing Refund
        Policy needs a consumer-jurisdiction carve-out section.
      </LAWYER>

      <H3>6. Governing Law &amp; Disputes</H3>
      <P>
        The Terms remain governed by the law specified therein, and disputes
        remain subject to the dispute-resolution procedures of the Terms —
        provided that, if you are a consumer in a jurisdiction whose law
        grants you non-waivable rights or a non-waivable forum (including EU
        and UK consumer law), nothing in the Terms deprives you of those
        protections, and you may bring proceedings in your local courts where
        the law so requires. The EU Online Dispute Resolution platform is
        available at https://ec.europa.eu/consumers/odr.
      </P>
      <LAWYER>
        Decide whether the existing arbitration clause should be disapplied
        entirely for EU/UK consumers (commonly done), and confirm governing
        law election + ODR/ADR disclosure obligations.
      </LAWYER>

      <H3>7. Language</H3>
      <P>
        The Services and all legal documents are provided in English. Any
        translation is for convenience only; the English version controls.
      </P>

      <H3>8. Changes; Severability</H3>
      <P>
        We may update this Addendum following the change procedure in the
        Terms. If any provision of this Addendum is unenforceable in your
        jurisdiction, the remainder continues in effect.
      </P>

      <div className="gold-hairline my-12" />

      {/* ============ DRAFT 2 — PRIVACY INTERNATIONAL ADDENDUM ============ */}
      <H>Draft 2 — International Privacy Addendum (GDPR / UK GDPR)</H>
      <P>
        This Addendum supplements the Armstrong K9 Registry Privacy Policy
        for individuals in the European Economic Area, the United Kingdom,
        and Switzerland, and for other jurisdictions whose law grants similar
        rights. If this Addendum conflicts with the Privacy Policy, this
        Addendum controls for those individuals.
      </P>

      <H3>1. Controller</H3>
      <P>
        The data controller is Armstrong K9 Registry [LEGAL ENTITY NAME],
        4054 Highway 273, Anderson, California, USA. Contact for privacy
        matters: the contact channels listed in the Privacy Policy.
      </P>
      <LAWYER>
        Insert the exact legal entity name/form. Determine whether an EU
        Article 27 representative and/or UK representative must be appointed
        (required for controllers outside the EU/UK that offer services to
        people there, unless processing is occasional and low-risk).
      </LAWYER>

      <H3>2. Legal Bases for Processing</H3>
      <P>
        We process personal data on these bases: <strong>performance of a
        contract</strong> (accounts, registrations, purchases, messaging);{" "}
        <strong>legitimate interests</strong> (fraud prevention, platform
        safety, service improvement, non-intrusive analytics);{" "}
        <strong>consent</strong> (marketing emails, non-essential cookies —
        withdrawable at any time); and <strong>legal obligation</strong>{" "}
        (tax, accounting, sanctions screening).
      </P>

      <H3>3. International Data Transfers</H3>
      <P>
        We are located in the United States and process data on servers in
        the United States. Where we transfer personal data from the EEA, UK,
        or Switzerland, we rely on appropriate safeguards: sub-processors
        certified under the EU-U.S. Data Privacy Framework where available,
        and the European Commission's Standard Contractual Clauses (with the
        UK Addendum) otherwise. Our sub-processor list is published at{" "}
        <Link to="/sub-processors" className="underline">/sub-processors</Link>.
      </P>
      <LAWYER>
        Confirm transfer mechanism per sub-processor (Stripe, Resend,
        Cloudflare, Sentry, hosting) and whether a Transfer Impact
        Assessment record is needed.
      </LAWYER>

      <H3>4. Your Rights</H3>
      <P>
        You may request: access to your data; rectification; erasure;
        restriction of processing; data portability; and objection to
        processing based on legitimate interests or for direct marketing.
        Exercise these via your account settings or the privacy contact. We
        respond within one month. You may also lodge a complaint with your
        local supervisory authority (EEA), the Information Commissioner's
        Office (UK), or the FDPIC (Switzerland).
      </P>

      <H3>5. Retention</H3>
      <P>
        We retain personal data as described in the Privacy Policy.
        Registry records (dog registrations, pedigrees, transfers) serve an
        ongoing record-keeping function analogous to a breed registry and
        are retained while the registry operates; account data is deleted or
        anonymized on verified deletion requests except where retention is
        legally required.
      </P>

      <H3>6. Cookies</H3>
      <P>
        Non-essential cookies are set only with consent via the cookie
        banner; consent can be withdrawn there at any time. See the{" "}
        <Link to="/cookies" className="underline">Cookie Notice</Link>.
      </P>

      <H3>7. Age</H3>
      <P>
        The Services require users to be at least 18 years old in all
        jurisdictions. We do not knowingly process children's data.
      </P>

      <H3>8. Automated Decision-Making</H3>
      <P>
        We do not make decisions producing legal or similarly significant
        effects based solely on automated processing. Fraud and content
        screening involve human review before adverse account action.
      </P>

      <div className="rounded-xl border border-[#EAEAEA] bg-white p-4 mt-10 text-[13px] leading-relaxed text-[#6B6B6B]">
        <strong className="text-ink">Reviewer checklist (suggested):</strong>{" "}
        entity name &amp; EU/UK representative · arbitration carve-out for
        EU/UK consumers · withdrawal-waiver wording · transfer mechanisms per
        sub-processor · DAC7/UK reporting if seller payouts ever expand
        beyond the US · per-country pet-sale law review before enabling
        listings in a new country (e.g., UK Lucy's Law).
      </div>
    </LegalLayout>
  );
}
